import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, AlertCircle, Trash2 } from "lucide-react";
import api, { errMsg } from "../lib/api";
import { inr, inrFull, num, pct } from "../lib/format";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { useAuth } from "../context/AuthContext";
import { DetailList, detailsComplete } from "./entry/DetailList";
import MtoClosing, { closureError } from "./entry/MtoClosing";

const GTYPE = (r, set) => (
  <div className="grid grid-cols-2 gap-2" data-testid="gms-detail-type">
    {[["new", "New GMS"], ["reenrollment", "Re-enrollment"]].map(([v, l]) => (
      <button key={v} type="button" onClick={() => set("gms_type", v)} className={`h-10 rounded-lg border text-sm font-semibold ${r.gms_type === v ? "bg-[#011E38] text-white border-[#011E38]" : "border-slate-200 text-slate-600"}`}>{l}</button>
    ))}
  </div>
);
const GPLAN = (r, set) => r.gms_type !== "new" ? null : (
  <div className="rounded-lg bg-[#F7F3EA] p-2.5 space-y-2" data-testid="gms-detail-plan">
    <div className="grid grid-cols-2 gap-2">
      <label className="block"><span className="text-xs font-semibold text-slate-600">GMS Plan ID</span>
        <Input value={r.plan_ref ?? ""} onChange={(e) => set("plan_ref", e.target.value)} className="mt-1 h-11 uppercase bg-white" data-testid="gms-details-plan_ref" /></label>
      <label className="block"><span className="text-xs font-semibold text-slate-600">Instalments</span>
        <Input type="number" min="1" value={r.instalments ?? ""} onChange={(e) => set("instalments", e.target.value)} className="mt-1 h-11 num bg-white" data-testid="gms-details-instalments" /></label>
    </div>
    <div className="text-xs text-[#8a6d2f]">Creates this plan in the GMS tracker with {Math.round(Number(r.instalments) || 0)} monthly payments of ₹{Math.round(Number(r.amount) || 0).toLocaleString("en-IN")} — instalment #1 marked paid.</div>
  </div>
);
const GMS_FIELDS = [["customer_name", "Customer Name"], ["mobile", "Mobile Number", "tel"], ["amount", "GMS Amount (₹)", "number"], ["start_date", "Plan Start Date", "date"], ["_custom", "type", null, GTYPE], ["_custom", "plan", null, GPLAN], ["remarks", "Remarks"]];
const MTO_FIELDS = [["customer_name", "Customer Name"], ["mobile", "Mobile Number", "tel"], ["mto_ref", "MTO Reference / Order ID", "text", { className: "uppercase" }], ["amount", "MTO Amount (₹)", "number"], ["remarks", "Remarks"]];

const Field = ({ label, value, onChange, testid, big }) => (
  <label className="block">
    <span className="text-sm font-semibold text-slate-600">{label}</span>
    <Input type="number" inputMode="decimal" min="0" value={value} onChange={(e) => onChange(e.target.value)} placeholder="0"
      className={`mt-1.5 ${big ? "h-16 text-2xl" : "h-14 text-xl"} num font-semibold`} data-testid={testid} />
  </label>
);

const EMPTY = { walkins: "", bills: "", sales: "", gms: "", mto: "", rts: "", npg: "", remarks: "" };

export default function EntryForm({ date, entry, cats, closed, onSaved, onDelete }) {
  const [f, setF] = useState(EMPTY);
  const [cv, setCv] = useState({});
  const [qv, setQv] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [gd, setGd] = useState([]);
  const [md, setMd] = useState([]);
  const [cl, setCl] = useState([]);
  const [closing, setClosing] = useState("");
  const [openMtos, setOpenMtos] = useState([]);
  const { settings } = useAuth();

  useEffect(() => {
    setF(entry ? Object.fromEntries(Object.keys(EMPTY).map((k) => [k, entry[k] ?? ""])) : EMPTY);
    setCv(entry?.categories || {});
    setQv(entry?.qty || {});
    setGd(entry?.gms_details || []);
    setMd(entry?.mto_details || []);
    const c = (entry?.mto_closures || []).map((t) => ({ mto_id: t.mto_id, closed: t.closed, cancelled: t.cancelled }));
    setCl(c);
    setClosing(entry ? (c.length ? "yes" : "no") : "");
    setErr("");
    api.get("/mto/open", { params: { exclude_entry: entry?.id } }).then((r) => setOpenMtos(r.data)).catch(() => setOpenMtos([]));
  }, [entry]);

  const n = (v) => Number(v) || 0;
  const catSum = cats.reduce((a, c) => a + n(cv[c.key]), 0);
  const diff = Math.round(catSum - n(f.sales));
  const totalQty = cats.reduce((a, c) => a + Math.round(n(qv[c.key])), 0);
  const set = (k) => (v) => setF({ ...f, [k]: v });
  const gmsAmt = n(f.gms), mtoAmt = n(f.mto);
  const sumOf = (rows) => Math.round(rows.reduce((a, r) => a + n(r.amount), 0));
  const gmsOk = gmsAmt <= 0 || (sumOf(gd) === Math.round(gmsAmt) && detailsComplete(gd, ["customer_name", "mobile", "start_date", "gms_type"])
    && gd.every((r) => r.gms_type !== "new" || (String(r.plan_ref || "").trim() && n(r.instalments) >= 1)));
  const mtoOk = mtoAmt <= 0 || (sumOf(md) === Math.round(mtoAmt) && detailsComplete(md, ["customer_name", "mobile", "mto_ref"]));
  const clOk = closing !== "" && (closing === "no" || (cl.length > 0 && cl.every((c) => !closureError(c, openMtos.find((m) => m.id === c.mto_id)))));
  const blankG = () => ({ customer_name: "", mobile: "", amount: "", gms_type: "new", start_date: date, remarks: "", plan_ref: "", instalments: settings.gms_default_instalments || 10 });
  const blankM = () => ({ customer_name: "", mobile: "", mto_ref: "", amount: "", remarks: "" });

  const preview = useMemo(() => {
    const s = n(f.sales), b = n(f.bills), w = n(f.walkins), u = totalQty;
    return { conv: w ? (b / w) * 100 : 0, atv: b ? s / b : 0, upt: b ? u / b : 0, asp: u ? s / u : 0 };
  }, [f, totalQty]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (diff !== 0) return setErr(`Category sales are ${inrFull(Math.abs(diff))} ${diff < 0 ? "short of" : "more than"} total sales.`);
    if (!gmsOk) return setErr("GMS detail amount does not match today's GMS amount.");
    if (!mtoOk) return setErr("MTO detail amount does not match today's MTO amount.");
    if (!clOk) return setErr(closing === "" ? "Answer: are you closing an existing MTO today?" : "Check the MTO closing amounts.");
    setBusy(true);
    try {
      const body = { date, remarks: f.remarks, categories: Object.fromEntries(cats.map((c) => [c.key, n(cv[c.key])])),
        qty: Object.fromEntries(cats.map((c) => [c.key, Math.round(n(qv[c.key]))])),
        gms_details: gmsAmt > 0 ? gd.map((r) => ({ ...r, amount: n(r.amount), instalments: r.gms_type === "new" ? Math.round(n(r.instalments)) : 0, plan_ref: r.gms_type === "new" ? (r.plan_ref || "") : "" })) : [],
        mto_details: mtoAmt > 0 ? md.map((r) => ({ ...r, amount: n(r.amount) })) : [],
        mto_closures: closing === "yes" ? cl.map((c) => ({ mto_id: c.mto_id, closed: n(c.closed), cancelled: n(c.cancelled) })) : [] };
      ["walkins", "bills"].forEach((k) => (body[k] = Math.round(n(f[k]))));
      ["sales", "gms", "mto", "rts", "npg"].forEach((k) => (body[k] = n(f[k])));
      await api.post("/entries", body);
      toast.success(entry ? "Entry updated" : "Entry submitted");
      onSaved();
    } catch (ex) {
      setErr(errMsg(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="daily-entry-form">
      <fieldset disabled={closed} className="space-y-6">
        <div className="bs-eyebrow">Customer activity</div>
        <div className="grid grid-cols-2 gap-3 !mt-2">
          <Field label="Walk-ins" value={f.walkins} onChange={set("walkins")} testid="entry-walkins-input" />
          <Field label="Number of Bills" value={f.bills} onChange={set("bills")} testid="entry-bills-input" />
        </div>
        <Field label="Total Gross Sales (₹)" value={f.sales} onChange={set("sales")} testid="entry-sales-input" big />
        <div className="bs-card p-4 space-y-3">
          <div className="bs-eyebrow">Sales by product (₹)</div>
          <div className="grid grid-cols-2 gap-3">
            {cats.map((c) => (
              <Field key={c.key} label={`${c.name} (₹)`} value={cv[c.key] ?? ""} onChange={(v) => setCv({ ...cv, [c.key]: v })} testid={`entry-cat-${c.key}-input`} />
            ))}
          </div>
          {n(f.sales) > 0 || catSum > 0 ? (
            diff === 0 ? (
              <div className="flex items-center gap-2 text-green-700 text-sm font-semibold" data-testid="category-match-ok"><CheckCircle2 className="w-4 h-4" /> Categories match total sales</div>
            ) : (
              <div className="flex items-center gap-2 text-red-700 text-sm font-semibold" data-testid="category-mismatch-error">
                <AlertCircle className="w-4 h-4" /> Category sales are {inrFull(Math.abs(diff))} {diff < 0 ? "short of" : "more than"} total sales.
              </div>
            )
          ) : null}
        </div>
        <div className="bs-card p-4 space-y-3" data-testid="entry-product-qty">
          <div className="bs-eyebrow">Product quantity</div>
          <div className="grid grid-cols-2 gap-3">
            {cats.map((c) => (
              <Field key={c.key} label={`${c.name}${c.key === "gold_coin" ? "s" : " Products"}`} value={qv[c.key] ?? ""} onChange={(v) => setQv({ ...qv, [c.key]: v })} testid={`entry-qty-${c.key}-input`} />
            ))}
          </div>
          <div className="flex items-center justify-between rounded-xl bg-[#011E38] text-white px-4 py-3">
            <span className="text-sm font-semibold">Total Products</span>
            <span className="num text-2xl font-bold" data-testid="entry-total-products">{totalQty}</span>
          </div>
        </div>
        <Field label="GMS / Plan Sales (₹)" value={f.gms} onChange={set("gms")} testid="entry-gms-input" />
        <DetailList title="GMS details" rows={gd} setRows={setGd} total={gmsAmt} blank={blankG} fields={GMS_FIELDS} sumLabel="Total GMS Details"
          mismatch="GMS detail amount does not match today's GMS amount." testid="gms-details" />
        <div className="grid grid-cols-3 gap-3">
          <Field label="MTO (₹)" value={f.mto} onChange={set("mto")} testid="entry-mto-input" />
          <Field label="RTS (₹)" value={f.rts} onChange={set("rts")} testid="entry-rts-input" />
          <Field label="NPG (₹)" value={f.npg} onChange={set("npg")} testid="entry-npg-input" />
        </div>
        <DetailList title="MTO order details" rows={md} setRows={setMd} total={mtoAmt} blank={blankM} fields={MTO_FIELDS} sumLabel="Total MTO Details"
          mismatch="MTO detail amount does not match today's MTO amount." testid="mto-details" />
        <MtoClosing answer={closing} setAnswer={setClosing} mtos={openMtos} rows={cl} setRows={setCl} />
        <label className="block">
          <span className="text-sm font-semibold text-slate-600">Remarks</span>
          <Textarea value={f.remarks} onChange={(e) => set("remarks")(e.target.value)} className="mt-1.5 text-base" rows={2} data-testid="entry-remarks-input" />
        </label>
        <div className="grid grid-cols-4 gap-2 text-center" data-testid="entry-kpi-preview">
          {[["Conv", pct(preview.conv)], ["ATV", inr(preview.atv)], ["UPT", num(preview.upt, 2)], ["ASP", inr(preview.asp)]].map(([l, v]) => (
            <div key={l} className="rounded-xl bg-slate-50 py-2.5"><div className="text-[0.65rem] text-slate-500 uppercase tracking-wider">{l}</div><div className="num font-bold text-[#011E38]">{v}</div></div>
          ))}
        </div>
        {err && <div className="rounded-xl bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm font-medium" data-testid="entry-submit-error">{err}</div>}
        <Button type="submit" disabled={busy || closed || diff !== 0 || !gmsOk || !mtoOk || !clOk} className="w-full h-16 text-lg rounded-2xl bg-[#011E38] hover:bg-[#032C52]" data-testid="entry-submit-button">
          {entry ? "Update entry" : "Submit entry"}
        </Button>
        {entry && (
          <Button type="button" variant="outline" onClick={onDelete} className="w-full h-12 rounded-2xl text-red-700 border-red-200 hover:bg-red-50" data-testid="entry-delete-button">
            <Trash2 className="w-4 h-4 mr-2" /> Delete this entry
          </Button>
        )}
      </fieldset>
    </form>
  );
}
